/**
 * App header: logo, title, profile.
 * В Mini App: название и подпись по центру между кнопками навигации Telegram, учёт safe area.
 * В админке: логотип ведёт на /admin, а не на главную; справа — кнопка перехода в профиль.
 * В PWA: кнопка «Назад» на страницах деталей (event, order, ticket, payment, profile/*).
 */

import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth.tsx";
import { isTelegramMiniApp, haptic } from "../../lib/telegram.ts";
import { isPWAStandalone } from "../../lib/pwa.ts";
import { getVisibleTabs, type MainTab } from "../../config/navigation.ts";
import {
  getVisibleAdminNavItems,
  type AdminNavIcon,
} from "../../config/admin-nav.ts";
import {
  IconUserCircle,
  IconUserCircleSolid,
  IconHome,
  IconHomeSolid,
  IconCalendar,
  IconCalendarSolid,
  IconReceipt,
  IconReceiptSolid,
  IconClipboardList,
  IconClipboardListSolid,
  IconTicket,
} from "../Icons.tsx";

const MAIN_ICON_MAP: Record<
  MainTab["icon"],
  React.ComponentType<{ className?: string }>
> = {
  home: IconHome,
  calendar: IconCalendar,
  clock: IconCalendar,
  user: IconUserCircle,
  userCircle: IconUserCircle,
};

const MAIN_ICON_MAP_SOLID: Record<
  MainTab["icon"],
  React.ComponentType<{ className?: string }>
> = {
  home: IconHomeSolid,
  calendar: IconCalendarSolid,
  clock: IconCalendarSolid,
  user: IconUserCircleSolid,
  userCircle: IconUserCircleSolid,
};

const ADMIN_ICON_MAP: Partial<
  Record<AdminNavIcon, React.ComponentType<{ className?: string }>>
> = {
  home: IconHome,
  payments: IconReceipt,
  orders: IconClipboardList,
  events: IconCalendar,
  tickets: IconTicket,
};

const ADMIN_ICON_MAP_SOLID: Partial<
  Record<AdminNavIcon, React.ComponentType<{ className?: string }>>
> = {
  home: IconHomeSolid,
  payments: IconReceiptSolid,
  orders: IconClipboardListSolid,
  events: IconCalendarSolid,
};

/** PWA fallback: маршруты с кнопкой «Назад» и куда вести при прямом заходе */
const PWA_BACK_ROUTES: { pattern: RegExp; fallback: string }[] = [
  { pattern: /^\/event\/[^/]+$/, fallback: "/" },
  { pattern: /^\/order\/[^/]+$/, fallback: "/orders" },
  { pattern: /^\/ticket\/[^/]+$/, fallback: "/orders" },
  { pattern: /^\/payment\/[^/]+\/thanks$/, fallback: "/orders" },
  { pattern: /^\/payment\/[^/]+$/, fallback: "/orders" },
  { pattern: /^\/profile\/settings$/, fallback: "/profile" },
  { pattern: /^\/profile\/email$/, fallback: "/profile" },
  { pattern: /^\/profile\/password$/, fallback: "/profile" },
  { pattern: /^\/church$/, fallback: "/" },
  { pattern: /^\/feedback$/, fallback: "/" },
  { pattern: /^\/pastor$/, fallback: "/" },
  { pattern: /^\/worship$/, fallback: "/" },
  { pattern: /^\/university$/, fallback: "/" },
  { pattern: /^\/prayers$/, fallback: "/" },
  { pattern: /^\/need-prayer$/, fallback: "/" },
  { pattern: /^\/donations$/, fallback: "/" },
  { pattern: /^\/materials$/, fallback: "/" },
  { pattern: /^\/home-groups$/, fallback: "/" },
  { pattern: /^\/home-group\/[^/]+$/, fallback: "/home-groups" },
  { pattern: /^\/clubs$/, fallback: "/" },
  { pattern: /^\/courses$/, fallback: "/" },
  { pattern: /^\/leadership-school$/, fallback: "/" },
  { pattern: /^\/womens-breakfasts$/, fallback: "/" },
  { pattern: /^\/master-classes$/, fallback: "/" },
  { pattern: /^\/family-club$/, fallback: "/" },
  { pattern: /^\/youth$/, fallback: "/" },
  { pattern: /^\/start-course$/, fallback: "/" },
  { pattern: /^\/admin\/events\/[^/]+\/orders$/, fallback: "/admin/events" },
  { pattern: /^\/admin\/events\/[^/]+$/, fallback: "/admin/events" },
  { pattern: /^\/admin\/categories\/[^/]+$/, fallback: "/admin/categories" },
  { pattern: /^\/admin\/home-groups\/[^/]+$/, fallback: "/admin/home-groups" },
  { pattern: /^\/admin\/ministries\/[^/]+$/, fallback: "/admin/ministries" },
  { pattern: /^\/admin\/orders\/cash-issue$/, fallback: "/admin/orders" },
  { pattern: /^\/admin\/content\/feedback$/, fallback: "/admin/content" },
  { pattern: /^\/admin\/content\/pastor$/, fallback: "/admin/content" },
  { pattern: /^\/admin\/content\/church$/, fallback: "/admin/content" },
  { pattern: /^\/admin\/content\/home-groups$/, fallback: "/admin/content" },
  { pattern: /^\/admin\/tickets\/validate$/, fallback: "/admin/tickets" },
  { pattern: /^\/admin\/tickets\/[^/]+\/view$/, fallback: "/admin/tickets" },
  { pattern: /^\/admin\/academy\/[^/]+\/analytics$/, fallback: "/admin/academy" },
  { pattern: /^\/admin\/academy\/[^/]+$/, fallback: "/admin/academy" },
];

function getPwaBackTarget(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  for (const { pattern, fallback } of PWA_BACK_ROUTES) {
    if (pattern.test(normalized)) return fallback;
  }
  return null;
}

export function AppHeader() {
  const { account } = useAuth();
  const isMiniApp = isTelegramMiniApp();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");
  const homePath = isAdmin ? "/admin" : "/";

  const fromState =
    location.state &&
    typeof location.state === "object" &&
    "from" in location.state &&
    typeof (location.state as { from?: string }).from === "string"
      ? (location.state as { from: string }).from
      : null;
  const pwaBackFallback =
    location.pathname.match(/^\/event\/[^/]+$/) && fromState
      ? fromState
      : getPwaBackTarget(location.pathname);
  const isPWA = isPWAStandalone();
  const mainTabs = getVisibleTabs(account ?? null);
  const adminNavItems = getVisibleAdminNavItems(
    account ? { permissions: account.permissions } : null
  );

  if (isMiniApp) {
    return (
      <header
        className="fixed top-0 left-0 right-0 z-40 flex-shrink-0 border-b border-slate-200/30 bg-transparent backdrop-blur-[6px]"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between gap-2 px-3 xs:px-4 min-h-[48px] xs:min-h-[52px]">
          {/* Левый блок: наша кнопка «Назад» (fallback) или spacer под tg.BackButton */}
          {pwaBackFallback ? (
            <button
              type="button"
              onClick={() => {
                haptic("light");
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(pwaBackFallback);
                }
              }}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg text-slate-600 active:bg-slate-200/50 transition-colors"
              aria-label="Назад"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          ) : (
            <div className="w-11 h-11 flex-shrink-0" aria-hidden />
          )}
          {/* Центр: название и подпись */}
          <Link
            to={homePath}
            className="flex-1 flex flex-col items-center justify-center min-w-0 py-1 no-underline text-inherit header-text"
          >
            <span className="font-bold tracking-tight text-[18px] xs:text-[20px] leading-tight text-slate-800 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">
              <span className="text-amber-600">One</span>
              <span>Church</span>
            </span>
            <span className="text-[11px] xs:text-xs text-slate-600 mt-0.5 tracking-wide font-medium truncate max-w-[180px] xs:max-w-[220px] text-center">
              Евангельская церковь
            </span>
          </Link>
          {isAdmin ? (
            <Link
              to="/profile"
              onClick={() => haptic("light")}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full text-slate-600 active:bg-slate-200/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30"
              aria-label="Перейти в профиль"
            >
              <IconUserCircle className="w-6 h-6" />
            </Link>
          ) : (
            <div className="w-11 h-11 flex-shrink-0" aria-hidden />
          )}
        </div>
      </header>
    );
  }

  // PWA: как в Mini App — по центру только название и подпись (без логотипа), те же отступы и кнопки
  if (isPWA) {
    return (
      <header
        className="fixed top-0 left-0 right-0 z-40 flex-shrink-0 border-b border-slate-200/30 bg-transparent backdrop-blur-[6px]"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between gap-2 px-3 xs:px-4 min-h-[48px] xs:min-h-[52px]">
          {pwaBackFallback ? (
            <button
              type="button"
              onClick={() => {
                haptic("light");
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(pwaBackFallback);
                }
              }}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg text-slate-600 active:bg-slate-200/50 transition-colors"
              aria-label="Назад"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          ) : (
            <div className="w-11 h-11 flex-shrink-0" aria-hidden />
          )}
          <Link
            to={homePath}
            className="flex-1 flex flex-col items-center justify-center min-w-0 py-1 no-underline text-inherit header-text"
          >
            <span className="font-bold tracking-tight text-[18px] xs:text-[20px] leading-tight text-slate-800 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">
              <span className="text-amber-600">One</span>
              <span>Church</span>
            </span>
            <span className="text-[11px] xs:text-xs text-slate-600 mt-0.5 tracking-wide font-medium truncate max-w-[180px] xs:max-w-[220px] text-center">
              Евангельская церковь
            </span>
          </Link>
          {isAdmin ? (
            <Link
              to="/profile"
              onClick={() => haptic("light")}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full text-slate-600 active:bg-slate-200/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30"
              aria-label="Перейти в профиль"
            >
              <IconUserCircle className="w-6 h-6" />
            </Link>
          ) : (
            <div className="w-11 h-11 flex-shrink-0" aria-hidden />
          )}
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-40 flex-shrink-0 safe-area-top border-b border-slate-200/30 bg-transparent backdrop-blur-[6px]">
      <div className="page-container flex items-center justify-between gap-2 xs:gap-3 h-[48px] xs:h-[52px] md-mobile:h-[56px] lg-mobile:h-[60px]">
        <div className="flex items-center gap-2 xs:gap-3 min-w-0 flex-1">
          {pwaBackFallback && (
            <button
              type="button"
              onClick={() => {
                haptic("light");
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(pwaBackFallback);
                }
              }}
              className="flex-shrink-0 w-10 h-10 xs:w-11 xs:h-11 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300/50"
              aria-label="Назад"
            >
              <svg
                className="w-5 h-5 xs:w-6 xs:h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
          <Link
            to={homePath}
            className={`flex items-center gap-2 xs:gap-3 no-underline group min-w-0 text-inherit ${
              pwaBackFallback ? "flex-1" : ""
            }`}
          >
            <div className="relative flex-shrink-0 header-logo">
              <img
                src="/logo.png"
                alt=""
                className="relative w-10 h-10 xs:w-11 xs:h-11 md-mobile:w-12 md-mobile:h-12 lg-mobile:w-14 lg-mobile:h-14 sm:w-12 sm:h-12 rounded-lg md-mobile:rounded-xl object-contain group-hover:scale-[1.02] transition-transform"
              />
            </div>
            <div className="min-w-0 header-text">
              <span className="font-bold tracking-tight text-[17px] xs:text-[18px] md-mobile:text-[20px] lg-mobile:text-[22px] leading-tight inline-block text-slate-800 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">
                <span className="text-amber-600">One</span>
                <span>Church</span>
              </span>
              <span className="hidden xs:block text-[11px] md-mobile:text-[12px] lg-mobile:text-[13px] text-slate-600 mt-0.5 tracking-wide font-medium truncate">
                Евангельская церковь
              </span>
            </div>
          </Link>
        </div>

        {/* Навбар в хэдере на десктопе */}
        <nav
          className="hidden lg:flex items-center gap-1 ml-auto"
          role="tablist"
          aria-label={isAdmin ? "Разделы админки" : "Основные разделы"}
        >
          {isAdmin
            ? adminNavItems.map((item) => {
                const IconOutline = ADMIN_ICON_MAP[item.icon];
                const IconSolid = ADMIN_ICON_MAP_SOLID[item.icon];
                if (!IconOutline) return null;
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.pathEnd ?? false}
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                      }`
                    }
                    role="tab"
                  >
                    {({ isActive }) => {
                      const Icon =
                        isActive && IconSolid ? IconSolid : IconOutline;
                      return (
                        <>
                          <Icon className="w-4 h-4" />
                          {item.label}
                        </>
                      );
                    }}
                  </NavLink>
                );
              })
            : mainTabs.map((tab) => {
                const IconOutline = MAIN_ICON_MAP[tab.icon];
                const IconSolid = MAIN_ICON_MAP_SOLID[tab.icon];
                return (
                  <NavLink
                    key={tab.id}
                    to={tab.path}
                    end={tab.pathEnd === true}
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                      }`
                    }
                    role="tab"
                  >
                    {({ isActive }) => {
                      const Icon = isActive ? IconSolid : IconOutline;
                      return (
                        <>
                          <Icon className="w-4 h-4" />
                          {tab.label}
                        </>
                      );
                    }}
                  </NavLink>
                );
              })}
        </nav>

        {isAdmin && (
          <Link
            to="/profile"
            className="flex-shrink-0 w-10 h-10 xs:w-11 xs:h-11 flex items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30"
            aria-label="Перейти в профиль"
          >
            <IconUserCircle className="w-6 h-6" />
          </Link>
        )}
      </div>
    </header>
  );
}
